import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Card,
  Container,
  IconButton,
  Snackbar,
  SnackbarCloseReason,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import {
  DataGridPremium,
  GridActionsCellItem,
  GridColDef,
  GridPaginationModel,
  GridRowParams,
  GridSortModel,
} from '@mui/x-data-grid-premium';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useDebounce } from 'use-debounce';
import { Add, QueryStats } from '@mui/icons-material';
import { sgrColors } from '../../../ui/sgr/SgrColors';
import { BreadcrumbsTitleContext } from '../../../wrappers/AppWrapper';
import { Contract, Customer } from '../../../api/hexa/models/CustomerModels';
import { ApiCustomer } from '../../../api/hexa/ApiCustomer';

const CustomerList = () => {
  const { t } = useTranslation();
  const [rows, setRows] = useState<Customer[]>([]);
  const [rowCount, setRowCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });
  const navigate = useNavigate();
  const { setCustomerName } = useContext(BreadcrumbsTitleContext);
  const [searchFullText, setSearchFullText] = useState<string>('');
  const [toggleValues, setToggleValues] = useState(() => ['active']);
  const [debounceSearchFullText] = useDebounce(searchFullText, 500);
  const [error, setError] = useState({
    open: false,
    message: '',
  });

  const onStatisticsClick = useCallback(
    async (rowId: string | number) => {
      setLoading(true);
      try {
        let customerNameFromApi =
          (await ApiCustomer.show(rowId)).name ?? `Customer ${rowId}`;
        setCustomerName(customerNameFromApi);
        localStorage.setItem('customerName', customerNameFromApi);
        navigate(`${rowId}/statistics`);
      } catch (error: any) {
        setError({
          open: true,
          message: `Error ${error.status ?? 500} fetching the customer stats: 
          ${error.response?.data.error ?? 'Error fetching the customer stats'}`,
        });
      } finally {
        setLoading(false);
      }
    },
    [setCustomerName, navigate]
  );

  const columns = useMemo<GridColDef<Customer>[]>(
    () => [
      { field: 'id', headerName: 'ID', flex: 1, maxWidth: 75 },
      { field: 'name', headerName: 'Customer Name', flex: 2 },
      { field: 'odoo_id', headerName: 'Odoo ID', flex: 1, maxWidth: 75 },
      {
        field: 'active',
        headerName: 'Active',
        flex: 1,
        type: 'boolean',
        maxWidth: 80,
      },
      {
        field: 'contracts__start_date',
        headerName: 'Start Date',
        type: 'date',
        flex: 1,
        valueGetter: (value, row) =>
          row.contract ? new Date(row.contract.start_date) : '',
        renderCell: (params: any) =>
          new Date(params.row.contract?.start_date).toLocaleString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          }),
      },
      {
        field: 'contracts__end_date',
        headerName: 'End Date',
        type: 'date',
        flex: 1,
        valueGetter: (value, row) =>
          row.contract ? new Date(row.contract.end_date) : '',
        renderCell: (params: any) =>
          new Date(params.row.contract?.end_date).toLocaleString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          }),
      },
      {
        field: 'expired_in',
        headerName: 'Contract status',
        flex: 1.5,
        renderCell: (params: any) => {
          const value = params.row.expired_in;
          const expiring = `Expiring in ${value} days`;
          if (value > 0 && value <= 7) {
            return <b>{expiring}</b>;
          } else if (value > 7) {
            return expiring;
          } else {
            return `Expired ${Math.abs(value)} days ago`;
          }
        },
      },
      {
        field: 'contracts__remaining_batch_screening_request',
        headerName: 'Remaining Batch',
        flex: 1,
        sortable: false,
        valueGetter: (value, row) =>
          row.contract ? row.contract.remaining_batch_screening_request : null,
        renderCell: (params: any) => {
          const lowCredits = columnLowCredits(params.row.contract);
          if (
            lowCredits.length > 0 &&
            lowCredits.includes('remaining_batch_screening_request')
          ) {
            return (
              <b>{params.row.contract?.remaining_batch_screening_request}</b>
            );
          }
        },
      },
      {
        field: 'contracts__remaining_monitoring_request',
        headerName: 'Remaining Monitoring',
        flex: 1,
        sortable: false,
        valueGetter: (value, row) =>
          row.contract ? row.contract.remaining_monitoring_request : null,
        renderCell: (params: any) => {
          const lowCredits = columnLowCredits(params.row.contract);
          if (
            lowCredits.length > 0 &&
            lowCredits.includes('remaining_monitoring_request')
          ) {
            return <b>{params.row.contract?.remaining_monitoring_request}</b>;
          }
        },
      },
      {
        field: 'contracts__remaining_screening_request',
        headerName: 'Remaining Screening',
        flex: 1,
        sortable: false,
        valueGetter: (value, row) =>
          row.contract ? row.contract.remaining_screening_request : null,
        renderCell: (params: any) => {
          const lowCredits = columnLowCredits(params.row.contract);
          if (
            lowCredits.length > 0 &&
            lowCredits.includes('remaining_screening_request')
          ) {
            return <b>{params.row.contract?.remaining_screening_request}</b>;
          }
        },
      },
      {
        field: 'actions',
        type: 'actions',
        maxWidth: 50,
        getActions: (params: GridRowParams) => [
          <GridActionsCellItem
            icon={
              <Tooltip title={t('customer_statistics.open')}>
                <QueryStats color="primary" />
              </Tooltip>
            }
            onClick={() => {
              onStatisticsClick(params.id);
            }}
            label={t('customer_statistics.open')}
          />,
        ],
      },
    ],
    [onStatisticsClick, t]
  );

  /**
   * Fetch the paginated customers from the API and set the rows state
   */
  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const { total, customers } = await ApiCustomer.index({
        page: paginationModel.page,
        size: paginationModel.pageSize,
        search: debounceSearchFullText,
        active: toggleValues.some((value) => value.includes('active')),
        expiring: toggleValues.some((value) => value.includes('expiring')),
        low_credits: toggleValues.some((value) =>
          value.includes('low_credits')
        ),
        sort_field: sortModel[0]?.field ?? 'id',
        sort_direction: sortModel[0]?.sort ?? 'asc',
      });
      setRowCount(total);
      setRows(customers);
    } catch (error: any) {
      setError({
        open: true,
        message: `Error ${error.status ?? 500} fetching the customers: 
        ${error.response?.data.error ?? 'Error fetching the customers'}`,
      });
    } finally {
      setLoading(false);
    }
  }, [
    paginationModel.page,
    paginationModel.pageSize,
    debounceSearchFullText,
    toggleValues,
    sortModel,
  ]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  /**
   * Close the error snackbar
   */
  const handleSnackbarClose = (
    event: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setError({
      open: false,
      message: error.message,
    });
  };

  const handleToggleValueChange = (newFormats: string[]) => {
    setToggleValues(newFormats);
  };

  const columnLowCredits = (contract: Contract | undefined): string[] => {
    const lowCredits: string[] = [];
    if (contract) {
      const remaining_screening_request = contract.remaining_screening_request;
      const remaining_batch_screening_request =
        contract.remaining_batch_screening_request;
      const remaining_monitoring_request =
        contract.remaining_monitoring_request;

      if (
        remaining_screening_request &&
        remaining_screening_request < (20 * contract.screening_quota) / 100
      ) {
        lowCredits.push('remaining_screening_request');
      }
      if (
        remaining_batch_screening_request &&
        remaining_batch_screening_request <
          (20 * contract.batch_screening_quota) / 100
      ) {
        lowCredits.push('remaining_batch_screening_request');
      }
      if (
        remaining_monitoring_request &&
        remaining_monitoring_request < (20 * contract.monitoring_quota) / 100
      ) {
        lowCredits.push('remaining_monitoring_request');
      }
    }
    return lowCredits;
  };

  const getClassName = (customer: Customer): string => {
    if (customer.expired_in < 0) {
      return 'expired-contract';
    } else if (
      (customer.expired_in >= 0 && customer.expired_in <= 7) ||
      columnLowCredits(customer.contract).length > 0
    ) {
      return 'expiring-contract';
    }
    return '';
  };

  return (
    <Container>
      <Card sx={{ padding: { xs: '0.5rem', sm: '1rem' } }}>
        <Stack
          spacing={1}
          sx={{
            //width: '90%',
            display: 'flex',
            flexDirection: 'column',
            margin: 'auto',
          }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            alignItems={{ xs: 'stretch', sm: 'center' }}
          >
            <TextField
              label="Search"
              variant="outlined"
              size="small"
              value={searchFullText}
              sx={{
                backgroundColor: 'white',
                width: { xs: '100%', sm: 'calc(100% - 300px)' },
              }}
              onChange={(e) => setSearchFullText(e.target.value)}
            />
            <ToggleButtonGroup
              size="small"
              sx={{ flexWrap: 'wrap' }}
              value={toggleValues}
              onChange={(e, value) => handleToggleValueChange(value)}
            >
              <ToggleButton value="active">
                {t('hexa_api.filters.active')}
              </ToggleButton>
              <ToggleButton value="expiring">
                {t('hexa_api.filters.expiring')}
              </ToggleButton>
              <ToggleButton value="low_credits">
                {t('hexa_api.filters.low_credits')}
              </ToggleButton>
            </ToggleButtonGroup>
            <IconButton
              onClick={() => navigate('new')}
              sx={{
                transition: 'transform 0.3s ease-in-out',
                '&:hover': {
                  color: sgrColors.azure,
                  backgroundColor: 'transparent',
                  transform: 'scale(1.2)',
                },
              }}
            >
              <Add />
            </IconButton>
          </Stack>
          <DataGridPremium
            getRowId={(row) => row.id}
            onRowClick={(params) =>
              window.open(`${window.location.href}/${params.row.id.toString()}`)
            }
            getRowClassName={(params) => getClassName(params.row)}
            sortingMode="server"
            sortModel={sortModel}
            onSortModelChange={setSortModel}
            loading={loading}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            rowCount={rowCount}
            paginationMode="server"
            pagination={true}
            pageSizeOptions={[10, 25, 50, 100]}
            autoHeight={true}
            rows={rows}
            columns={columns}
            density={'compact'}
          />
        </Stack>
        <Snackbar
          open={error.open}
          autoHideDuration={5000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={handleSnackbarClose} severity="error">
            {error.message}
          </Alert>
        </Snackbar>
      </Card>
    </Container>
  );
};

export default CustomerList;
